import 'react-image-crop/dist/ReactCrop.css'

import { Button, Stack, styled, Typography } from '@mui/material'
import {
  FC,
  RefObject,
  SyntheticEvent,
  useCallback,
  useRef,
  useState,
} from 'react'
import ReactCrop, { convertToPixelCrop, Crop } from 'react-image-crop'
import { IconButton, Icons } from 'utoniq-ui'

import { BottomSafeArea } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { FullscreenModal } from '@/components/organisms/functionals'
import { getCropBySize } from '@/components/organisms/imageEditor/ImageEditModal'
import { ArtistThemeProvider } from '@/components/styles'
import { useMessage, useTranslation } from '@/core'
import { Cell, CellFrame } from '@/core/architecture'
import { useDeviceInfo } from '@/core/native/device'

type ConsoleImageEditModalProps = {
  open: boolean
  onClose: () => void
  src: string
  artistId: string
  onSave: (data: { tmpPath: string; fileUrl: string }, localFile: Blob) => void
  onUploadFile: (
    file: Blob | File
  ) => Promise<{ tmpPath: string; fileUrl: string }>
}

enum RatioType {
  ORIGINAL,
  RECTANGLE,
}

export const ConsoleImageEditModal: FC<ConsoleImageEditModalProps> = ({
  open,
  src,
  artistId,
  onClose,
  onSave,
  onUploadFile,
}) => {
  const { t } = useTranslation('threadCreate')
  const { deviceInfo } = useDeviceInfo()
  const [saving, setSaving] = useState<boolean>(false)
  const [ratioType, setRatioType] = useState<RatioType>(RatioType.ORIGINAL)
  const [rotate, setRotate] = useState<number>(0)
  const { showError } = useMessage()
  const imageRef = useRef<HTMLImageElement>() as RefObject<HTMLImageElement>
  const cropRef = useRef<Crop>()
  const [aspect, setAspect] = useState<number | undefined>(undefined)
  const [crop, setCrop] = useState<Crop>({
    x: 0,
    y: 0,
    width: 100,
    height: 100,
    unit: '%',
  })

  const handleImageLoaded = useCallback(
    (event: SyntheticEvent<HTMLImageElement>) => {
      const newCrop = getCropBySize(
        event.currentTarget.width,
        event.currentTarget.height,
        event.currentTarget.width / event.currentTarget.height
      )
      setAspect(undefined)
      cropRef.current = newCrop
      setCrop(newCrop)
      return false
    },
    []
  )

  const handleClose = useCallback(() => {
    setAspect(undefined)
    setRatioType(RatioType.ORIGINAL)
    setRotate(0)
  }, [])

  const handleCropImage = useCallback(async () => {
    if (!imageRef.current || !cropRef.current) {
      return
    }
    setSaving(true)
    try {
      const newCrop = convertToPixelCrop(
        cropRef.current,
        imageRef.current.width,
        imageRef.current.height
      )
      const localFile = await getCroppedImg(
        imageRef.current,
        newCrop,
        deviceInfo?.operatingSystem === 'ios',
        undefined,
        rotate
      )
      const data = await onUploadFile(localFile)
      onSave(data, localFile)
      handleClose()
    } catch (err) {
      /**
       * @todo エラーハンドリングの型付け
       * https://app.clickup.com/t/908127/UT-5180
       */
      if (typeof err === 'string') {
        showError(new Error(t(err)))
      } else {
        showError(err as Error)
      }
    }
    setSaving(false)
  }, [
    deviceInfo?.operatingSystem,
    onUploadFile,
    onSave,
    showError,
    t,
    rotate,
    handleClose,
  ])

  const handleChangeRatio = useCallback((ratioType: RatioType) => {
    if (!imageRef.current) {
      return
    }
    const newCrop = getCropBySize(
      imageRef.current.width,
      imageRef.current.height,
      ratioType === RatioType.RECTANGLE
        ? 16 / 9
        : imageRef.current.width / imageRef.current.height
    )
    setAspect(ratioType === RatioType.RECTANGLE ? 16 / 9 : undefined)
    cropRef.current = newCrop
    setCrop(newCrop)
    setRatioType(ratioType)
  }, [])

  const handleChangeRotate = useCallback(() => {
    setRotate(prevState => prevState + 90)
  }, [])

  const renderBottomItem = useCallback(
    () => (
      <FixedBottomCell
        ratioType={ratioType}
        onChangeRatioType={handleChangeRatio}
        onChangeRotate={handleChangeRotate}
      />
    ),
    [ratioType, handleChangeRatio, handleChangeRotate]
  )

  return (
    <FullscreenModal
      open={open}
      onClose={() => {
        onClose()
        handleClose()
      }}
    >
      <ArtistThemeProvider artistId={artistId}>
        <ConsoleBaseLayout
          header={{
            left: (
              <IconButton.Close edge="start" color="white" onClick={onClose} />
            ),
            title: t('imageEditTitle', '切り抜き'),
            right: (
              <Button
                variant="text"
                loading={saving}
                color="primary"
                sx={{ mr: -1, color: 'white' }}
                onClick={handleCropImage}
              >
                {t('keep')}
              </Button>
            ),
          }}
          customNavigation={
            <FixedBottomCell
              ratioType={ratioType}
              onChangeRatioType={handleChangeRatio}
              onChangeRotate={handleChangeRotate}
            />
          }
        >
          <Stack
            flex={1}
            justifyContent="center"
            sx={{
              height: '100%',
              width: '100%',
              bgcolor: 'grey.900',
              p: 5,
            }}
          >
            <ReactCropStyled
              crop={crop}
              ruleOfThirds
              aspect={aspect}
              minHeight={200}
              onChange={(_, newCrop) => {
                setCrop(newCrop)
                cropRef.current = newCrop
              }}
            >
              <img
                ref={imageRef}
                src={src}
                alt=""
                style={{ transform: `rotate(${rotate}deg)` }}
                onLoad={handleImageLoaded}
              />
            </ReactCropStyled>
          </Stack>
        </ConsoleBaseLayout>
      </ArtistThemeProvider>
    </FullscreenModal>
  )
}

const TO_RADIANS = Math.PI / 180

async function getCroppedImg(
  image: HTMLImageElement,
  crop: Crop,
  isIOS: boolean,
  maximumPixels = 16777216,
  rotate = 0
): Promise<Blob> {
  const canvas = document.createElement('canvas')
  const pixelRatio = window.devicePixelRatio
  const scaleX = image.naturalWidth / image.width
  const scaleY = image.naturalHeight / image.height
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    return Promise.reject('Can not get context')
  }

  const requiredPixels =
    image.naturalWidth * pixelRatio * image.naturalHeight * pixelRatio
  let scalar = 1
  if (requiredPixels > maximumPixels && isIOS) {
    scalar = Math.sqrt(maximumPixels) / Math.sqrt(requiredPixels)
  }

  canvas.width = Math.floor(crop.width * scaleX * pixelRatio * scalar)
  canvas.height = Math.floor(crop.height * scaleY * pixelRatio * scalar)

  ctx.scale(pixelRatio, pixelRatio)
  ctx.imageSmoothingQuality = 'high'

  const cropX = crop.x * scaleX
  const cropY = crop.y * scaleY

  const rotateRads = rotate * TO_RADIANS

  const centerX = image.naturalWidth / 2
  const centerY = image.naturalHeight / 2

  ctx.save()

  // Move the crop origin to the canvas origin (0,0)
  ctx.translate(-cropX, -cropY)
  // Move the origin to the center of the original position
  ctx.translate(centerX, centerY)
  // Rotate around the origin
  ctx.rotate(rotateRads)
  // Move the center of the image to the origin (0,0)
  ctx.translate(-centerX, -centerY)
  ctx.drawImage(
    image,
    0,
    0,
    image.naturalWidth * scalar,
    image.naturalHeight * scalar,
    0,
    0,
    image.naturalWidth * scalar,
    image.naturalHeight * scalar
  )
  // As a blob
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      blob => {
        if (!blob) {
          return reject(
            /**
             * @todo
             * iOSサイズ制限のため縦横幅が大きい画像をcanvasで出力できない。トリムなしで画像アップできるようにリサイズ処理などを実装する。
             *
             * https://app.clickup.com/t/908127/UT-5179
             */
            crop.width * scaleX > 2355 ? 'canvasSizeOver' : 'canvasIsEmpty'
          )
        }
        return resolve(blob)
      },
      'image/jpeg',
      1
    )
  })
}

type FixedBottomCellProps = {
  ratioType: RatioType
  onChangeRatioType: (ratioType: RatioType) => void
  onChangeRotate: () => void
}
const FixedBottomCell: Cell<FixedBottomCellProps> = ({
  ratioType,
  onChangeRatioType,
  onChangeRotate,
}) => {
  const { t } = useTranslation('consoleCollectionCreate')
  return (
    <BottomSafeArea sx={{ bgcolor: 'grey.900' }}>
      <CellFrame
        sx={{ py: 4, px: 5, bgcolor: 'grey.900', textAlign: 'center' }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Stack direction="row" alignItems="center" spacing={2}>
            <StyledButton
              variant="text"
              sx={{
                color:
                  ratioType === RatioType.ORIGINAL ? 'primary.main' : 'white',
              }}
              onClick={() => onChangeRatioType(RatioType.ORIGINAL)}
            >
              <Icons.Reset fontSize="small" color="inherit" />
              <Typography variant="overline" sx={{ pt: 1 }} color="inherit">
                {t('originalSize', '元サイズ')}
              </Typography>
            </StyledButton>
            <StyledButton
              variant="text"
              sx={{
                color:
                  ratioType === RatioType.RECTANGLE ? 'primary.main' : 'white',
              }}
              onClick={() => onChangeRatioType(RatioType.RECTANGLE)}
            >
              <Icons.Rectangle fontSize="small" color="inherit" />
              <Typography variant="overline" sx={{ pt: 1 }} color="inherit">
                {t('rectangleSize', '16:9')}
              </Typography>
            </StyledButton>
          </Stack>
          <Stack>
            <StyledButton
              variant="text"
              sx={{ color: 'white' }}
              onClick={onChangeRotate}
            >
              <Icons.Rotate variant="v2" fontSize="small" color="inherit" />
              <Typography variant="overline" sx={{ pt: 1 }} color="inherit">
                {t('rotate', '回転')}
              </Typography>
            </StyledButton>
          </Stack>
        </Stack>
      </CellFrame>
    </BottomSafeArea>
  )
}

const StyledButton = styled(Button)(() => ({
  flexDirection: 'column',
  p: 0,
  borderRadius: 0,
  textAlign: 'center',
  minWidth: 56,
  alignItems: 'center',
}))

const ReactCropStyled = styled(ReactCrop)(() => ({
  '&&': {
    maxHeight: '100%',
    '.ReactCrop__drag-handle': {
      margin: 0,
      '&::after': {
        width: 40,
        height: 40,
        borderWidth: 0,
        borderStyle: 'solid',
        borderColor: 'white',
        backgroundColor: 'transparent',
      },
    },
    '.ReactCrop__crop-selection': {
      borderStyle: 'solid',
    },
    '& .ReactCrop__crop-selection': {
      animation: 'none',
      backgroundImage: 'none',
      borderWidth: 2,
    },
  },
}))
